import {
  EventPayloadType,
  type Event,
  type GetEventsQueryVariables,
} from "@package/ui-gql/index-analytics";
import { type SliceWallScreenState } from "@package/ui-wall-engine";
import { produce, type WritableDraft } from "immer";
import moment from "moment";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { useGetEvents } from "../../../../../../apps/ui/cms/src/hooks/use-get-events";
import {
  defaultTriviaContextState,
  TriviaContext,
  type TriviaState,
} from "./context-trivia";

type TriviaProviderProps = {
  screen?: SliceWallScreenState;
};

export function TriviaContextProvider({
  screen,
  children,
}: PropsWithChildren<TriviaProviderProps>) {
  const [state, setState] = useState<TriviaState>({
    ...defaultTriviaContextState,
    screen,
  });

  const variablesEvents = useMemo<GetEventsQueryVariables>(
    () => ({
      page: 0,
      perPage: 1000,
      sortField: "timestamp",
      sortOrder: "desc",
      filter: {
        eventType: [EventPayloadType.TriviaQuestion],
        post: screen?.flyout?.post?.id ? [screen.flyout.post.id] : [],
        range: {
          end: moment().toDate(),
          start: moment().subtract(7, "days").toDate(),
        },
      },
    }),
    [screen],
  );

  const { data, loading } = useGetEvents(variablesEvents);

  const updateTriviaState = useCallback(
    (callback: (draft: WritableDraft<TriviaState>) => void) => {
      setState((prev) => produce(prev, callback));
    },
    [],
  );

  useEffect(
    function effectOnStateChangedExternal() {
      setState((prev) => ({
        ...prev,
        screen,
        updateTriviaState,
      }));
    },
    [screen, updateTriviaState],
  );

  useEffect(
    function effectOnAnalyticsLoadingChanged() {
      setState((prev) => ({
        ...prev,
        analyticsLoading: loading,
      }));
    },
    [loading],
  );

  useEffect(
    function effectOnAnalyticsDataChanged() {
      if (data?.items?.length) {
        setState((prev) => ({
          ...prev,
          analytics: data.items as Event[],
        }));
      }
    },
    [data],
  );
  return (
    <TriviaContext.Provider value={state}>{children}</TriviaContext.Provider>
  );
}
